/*----------------------------------------------------------------------------
    EditAddMeat.tsx

----------------------------------------------------------------------------*/

import React from 'react';
import '../App.css';
import { makeStyles, Text, Divider, Dialog, DialogTrigger, Button, DialogSurface, DialogBody, DialogTitle, DialogContent, Field, Input, DialogActions, Dropdown, Option, SelectionEvents, OptionOnSelectData } from
    '@fluentui/react-components';
import { InputTypes, RexInput } from '../components/RexInput';
import { FormControls } from "../model/FormControls";
import { IAppContext } from "../model/IAppContext";
import { TheAppContext } from "../model/AppContext";
import { withStyles } from '../components/withStyles';
import { Meat } from "../model/Meat";
import { RexDateTime } from '../Dates/RexDateTime';

export interface OnDismissDelegate
{
    (): void;
}

export interface OnSaveDelegate
{
    (newItem: Meat): void;
}

export interface EditAddMeatProps
{
    styles: Record<string, string>;
    isAdd: boolean;
    open?: boolean;
    onSave: OnSaveDelegate;
    onDismiss: OnDismissDelegate;
    itemEditing: Meat;
}

export interface EditAddMeatState
{
    itemEditing: Meat;
    isOpen: boolean;
}

const useStyles = makeStyles(
    {
        stackHorizontal:
        {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between'
        },
        stackVertical:
        {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
        }
        //        dividerCustom: {
        //            fontSize: '14px',
        //            fontWeight: 'bold'
        //        },
        //        insetContent: {
        //            marginLeft: '.1in',
        //            marginRight: '.1in'
        //        }

    });

class EditAddMeatWithoutStyles extends React.Component<EditAddMeatProps, EditAddMeatState>
{
    context!: IAppContext;
    static contextType = TheAppContext;
    m_controls: FormControls<Meat>;

    constructor(props: EditAddMeatProps)
    {
        super(props);
        this.state =
        {
            isOpen: this.props.open ?? false,
            itemEditing: this.props.itemEditing,
        };

        this.m_controls = new FormControls<Meat>(
            [
                ["SmokeID", "smokeID"],
                ["MeatID", "smokeID"],
                ["Description", "description"],
                ["Source", "source"],
                ["Rub", "rub"],
                ["TrimmedGrams", "trimmedGrams"],
                ["UntrimmedGrams", "untrimmedGrams"],
                ["InitialObs", "initialObs"],
                ["Preparation", "preparation"],
                ["PostSmokeObs", "postSmokeObs"]
            ]);
    }

    async componentDidMount()
    {
        this.m_controls.updateForData(this.state.itemEditing);
    }

    async componentDidUpdate(prevProps: EditAddMeatProps, prevState: EditAddMeatState)
    {
        if (prevProps.itemEditing != this.props.itemEditing)
        {
            this.m_controls.updateForData(this.state.itemEditing);
            this.setState(
                {
                    itemEditing: this.props.itemEditing,
                });
        }
    }

    async onCommit(formEvent: React.FormEvent)
    {
        formEvent.preventDefault(); // don't auto dismiss

        // need to query the DOM and populate the item from that to allow
        // autofill (which doesn't fire any events)
        const newItem = this.m_controls.getDataFromFormForEvent(formEvent);

        newItem.UntrimmedGrams = RexInput.fieldValueFromInputValue(InputTypes.Integer, newItem.UntrimmedGrams as unknown as string);
        newItem.TrimmedGrams = RexInput.fieldValueFromInputValue(InputTypes.Integer, newItem.TrimmedGrams as unknown as string);

        newItem.SmokeID = this.state.itemEditing.SmokeID;

        this.props.onSave(newItem);
    }

    handleInputChange(field: string, value: string)
    {
        const valueToUse =
            (field == "UntrimmedGrams" || field == "TrimmedGrams")
                ? RexInput.fieldValueFromInputValue(InputTypes.Integer, value)
                : value;

        this.setState(
            (prevState) => ({
                itemEditing:
                {
                    ...prevState.itemEditing,
                    [field]: valueToUse
                }
            }));
    }

    dismissDialog()
    {
        this.props.onDismiss();
    }

    render()
    {
        const smoke = this.state.itemEditing;
        const onChange = this.handleInputChange.bind(this);
        const dismissOpen = () => this.dismissDialog();
        const adding = this.props.isAdd;

        return (
            <Dialog modalType="modal" open={this.props.open}>
                <DialogSurface>
                    <form onSubmit={(event) => this.onCommit(event)}>
                        <DialogBody>
                            <DialogTitle>Meat</DialogTitle>
                            <DialogContent className={this.props.styles.content}>
                                <RexInput label="Meat ID" controls={this.m_controls} controlName="MeatID" style={{ width: '16em' }} onChange={onChange} value={smoke.MeatID}/>
                                <RexInput label="Description" controls={this.m_controls} controlName="Description" style={{ width: '32em' }} onChange={onChange} value={smoke.Description}/>
                                <RexInput label="Source" controls={this.m_controls} controlName="Source" style={{ width: '32em' }} onChange={onChange} value={smoke.Source}/>
                                <RexInput label="Rub" controls={this.m_controls} controlName="Rub" style={{ width: '32em' }} onChange={onChange} value={smoke.Rub}/>
                                <div className={this.props.styles.stackHorizontal}>
                                    <RexInput label="Untrimmed Grams" controls={this.m_controls} controlName="UntrimmedGrams" style={{ width: '16em' }} onChange={onChange} value={smoke.UntrimmedGrams}/>
                                    <RexInput label="Trimmed Grams" controls={this.m_controls} controlName="TrimmedGrams" style={{ width: '16em' }} onChange={onChange} value={smoke.TrimmedGrams}/>
                                </div>
                                <RexInput label="Initial Obs" controls={this.m_controls} controlName="InitialObs" style={{ width: '32em' }} onChange={onChange} value={smoke.InitialObs}/>
                                <RexInput label="Preparation" controls={this.m_controls} controlName="Preparation" style={{ width: '32em' }} onChange={onChange} value={smoke.Preparation}/>
                                <RexInput label="Post Smoke Obs" controls={this.m_controls} controlName="PostSmokeObs" style={{ width: '32em' }} onChange={onChange} value={smoke.PostSmokeObs}/>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={dismissOpen} appearance="secondary">Cancel</Button>
                                <Button type="submit" appearance="primary">Save</Button>
                            </DialogActions>
                        </DialogBody>
                    </form>
                </DialogSurface>
            </Dialog>)
    }
}

export const EditAddMeat = withStyles(useStyles, EditAddMeatWithoutStyles);